import { Action, handleActions } from 'redux-actions'
import {
  getCompanyDomains,
  getCompanyDomainsSuccess,
  getCompanyDomainsFail,
  clearAddedDomain,
  addCompanyDomain,
  addCompanyDomainSuccess,
  addCompanyDomainFail,
  removeCompanyDomain,
  removeCompanyDomainSuccess,
  removeCompanyDomainFail,
  verifyCompanyDomain,
  verifyCompanyDomainSuccess,
  verifyCompanyDomainFail,
  getCompanyDomainSettings,
  getCompanyDomainSettingsSuccess,
  getCompanyDomainSettingsFail,
  updateCompanyDomainSettings,
  updateCompanyDomainSettingsSuccess,
  updateCompanyDomainSettingsFail,
} from './DomainAdminActions'
import { CompanyDomain } from '../../../services/api.types'

/**
 * Domain Admin state.
 * Stores the list of domains for the selected company.
 */
class State {
  // Domains for the selected company
  domains: CompanyDomain[] = []
  loading: boolean = false
  adding: boolean = false
  removing: boolean = false
  verifying: boolean = false
  // Newly added domain (to show DNS instructions in the dialog)
  addedDomain: CompanyDomain | null = null
  // Domain settings
  emailSignupDomain: boolean = false
  googleSignupDomain: boolean = false
  settingsLoading: boolean = false
  settingsUpdating: boolean = false
}

export const domainAdmin = handleActions<State, any>(
  {
    // --- Get Domains ---
    [String(getCompanyDomains)]: (state: State): State => ({
      ...state,
      loading: true,
    }),
    [String(getCompanyDomainsSuccess)]: (state: State, action: Action<any>): State => ({
      ...state,
      loading: false,
      adding: false,
      removing: false,
      verifying: false,
      domains: action.payload?.domains || [],
    }),
    [String(getCompanyDomainsFail)]: (state: State): State => ({
      ...state,
      loading: false,
      adding: false,
      removing: false,
      verifying: false,
      domains: [],
    }),

    // --- Clear Added Domain ---
    [String(clearAddedDomain)]: (state: State): State => ({
      ...state,
      addedDomain: null,
    }),

    // --- Add Domain ---
    [String(addCompanyDomain)]: (state: State): State => ({
      ...state,
      adding: true,
      addedDomain: null,
    }),
    [String(addCompanyDomainSuccess)]: (state: State, action: Action<any>): State => ({
      ...state,
      adding: false,
      addedDomain: action.payload || null,
    }),
    [String(addCompanyDomainFail)]: (state: State): State => ({
      ...state,
      adding: false,
      addedDomain: null,
    }),

    // --- Remove Domain ---
    [String(removeCompanyDomain)]: (state: State): State => ({
      ...state,
      removing: true,
    }),
    [String(removeCompanyDomainSuccess)]: (state: State): State => ({
      ...state,
      removing: false,
    }),
    [String(removeCompanyDomainFail)]: (state: State): State => ({
      ...state,
      removing: false,
    }),

    // --- Verify Domain ---
    [String(verifyCompanyDomain)]: (state: State): State => ({
      ...state,
      verifying: true,
    }),
    [String(verifyCompanyDomainSuccess)]: (state: State): State => ({
      ...state,
      verifying: false,
    }),
    [String(verifyCompanyDomainFail)]: (state: State): State => ({
      ...state,
      verifying: false,
    }),

    // --- Get Domain Settings ---
    [String(getCompanyDomainSettings)]: (state: State): State => ({
      ...state,
      settingsLoading: true,
    }),
    [String(getCompanyDomainSettingsSuccess)]: (state: State, action: Action<any>): State => ({
      ...state,
      settingsLoading: false,
      emailSignupDomain: action.payload?.emailSignupDomain ?? false,
      googleSignupDomain: action.payload?.googleSignupDomain ?? false,
    }),
    [String(getCompanyDomainSettingsFail)]: (state: State): State => ({
      ...state,
      settingsLoading: false,
    }),

    // --- Update Domain Settings ---
    [String(updateCompanyDomainSettings)]: (state: State): State => ({
      ...state,
      settingsUpdating: true,
    }),
    [String(updateCompanyDomainSettingsSuccess)]: (state: State, action: Action<any>): State => ({
      ...state,
      settingsUpdating: false,
      emailSignupDomain: action.payload?.emailSignupDomain ?? state.emailSignupDomain,
      googleSignupDomain: action.payload?.googleSignupDomain ?? state.googleSignupDomain,
    }),
    [String(updateCompanyDomainSettingsFail)]: (state: State): State => ({
      ...state,
      settingsUpdating: false,
    }),
  },
  new State(),
)

export { State as DomainAdminState }
